import React, { useState, useEffect } from 'react';
import { stockDisponivel } from '../utils/stock';
import { resolveImage } from '../utils/image';
import { useNavigate, useParams } from 'react-router-dom';
import {
  FiArrowLeft,
  FiShoppingCart,
  FiPlus,
  FiMinus,
  FiShare2,
  FiTag,
  FiPackage,
  FiCheck,
  FiAlertTriangle,
  FiXCircle,
  FiDownload,
  FiZap,
} from 'react-icons/fi';
import { IoStorefrontOutline } from 'react-icons/io5';
import { ecommerceService } from '../services';
import { useCart } from '../contexts/CartContext';
import type { Product } from '../types';
import { LoaderComponent, EmptyState } from '../components/ui';

const formatCurrency = (value: number): string =>
  new Intl.NumberFormat('pt-CV', {
    style: 'currency',
    currency: 'CVE',
    minimumFractionDigits: 0,
  }).format(value);

const ProductDetail: React.FC = () => {
  const navigate = useNavigate();
  const { storeId, productId } = useParams<{ storeId: string; productId: string }>();
  const { addToCart, cart, itemCount, cartTotal } = useCart();

  const [product, setProduct] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [quantity, setQuantity] = useState(1);
  const [addedToCart, setAddedToCart] = useState(false);

  useEffect(() => {
    if (storeId && productId) {
      loadProduct();
    }
  }, [storeId, productId]);

  const loadProduct = async () => {
    setLoading(true);
    try {
      const response = await ecommerceService.getProductDetail(storeId!, productId!);
      setProduct(response);
      sessionStorage.setItem('pageTitle', response.NOME || 'Produto');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar produto');
    } finally {
      setLoading(false);
    }
  };

  const isDigital = product?.tipo === 'DIGITAL' || product?.TIPO === 'DIGITAL';

  const handleAddToCart = () => {
    if (!product) return;
    const cartProduct: Product = {
      ID: product.ID,
      NOME: product.NOME,
      DESCRICAO: product.DESCRICAO,
      PRECO: product.PRECO,
      PRECO_PROMOCIONAL: product.PRECO_PROMOCIONAL,
      IMAGEM: product.IMAGEM,
      MIME_TYPE: product.MIME_TYPE,
      STOCK: product.STOCK,
      LOJA_ID: storeId,
      TIPO: product.tipo || product.TIPO,
    };
    addToCart(cartProduct, isDigital ? 1 : quantity);
    setAddedToCart(true);
    setTimeout(() => setAddedToCart(false), 2000);
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: product?.NOME || 'Produto',
          text: product?.DESCRICAO || 'Veja este produto no Pagali Shop',
          url: window.location.href,
        });
      } catch { /* cancelled */ }
    } else {
      await navigator.clipboard.writeText(window.location.href);
    }
  };

  const price = product?.PRECO || 0;
  const hasPromotion = product?.PRECO_PROMOCIONAL && product.PRECO_PROMOCIONAL < price;
  const currentPrice = hasPromotion ? product.PRECO_PROMOCIONAL : price;
  const discountPercent = hasPromotion
    ? Math.round((1 - product.PRECO_PROMOCIONAL / price) * 100)
    : 0;
  const stock = stockDisponivel(product);
  const isOutOfStock = !isDigital && stock <= 0;

  const imageSrc = resolveImage(product?.IMAGEM, product?.MIME_TYPE);
  const lojaLogoSrc = resolveImage(product?.LOJA_LOGO, product?.LOJA_MIME_TYPE);

  // Already in cart?
  const inCart = cart.find((item) => item.ID === product?.ID);

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <LoaderComponent size="md" text="A carregar produto..." />
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="space-y-6">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-pagali-gray-600 hover:text-pagali-gray-800 transition-colors"
        >
          <FiArrowLeft />
          <span>Voltar</span>
        </button>
        <EmptyState
          icon={<FiPackage className="text-5xl" />}
          title="Produto nao encontrado"
          description={error || 'O produto que procura nao existe ou foi removido'}
          action={
            <button onClick={() => navigate('/ecommerce')} className="btn-primary mt-4">
              Ver todas as lojas
            </button>
          }
        />
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-24">
      {/* Back */}
      <button
        onClick={() => navigate(-1)}
        className="flex items-center gap-2 text-pagali-gray-600 hover:text-pagali-gray-800 transition-colors"
      >
        <FiArrowLeft />
        <span>Voltar</span>
      </button>

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Image */}
        <div className="relative">
          <div className="aspect-[3/4] bg-gray-100 rounded-2xl overflow-hidden shadow-card">
            {imageSrc ? (
              <img
                src={imageSrc}
                alt={product.NOME}
                loading="lazy"
                className="w-full h-full object-cover object-top"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center bg-gray-50">
                <FiPackage className="text-8xl text-gray-200" />
              </div>
            )}
          </div>

          {/* Digital Badge */}
          {isDigital && (
            <div className="absolute top-4 left-4 inline-flex items-center gap-1 bg-emerald-500 text-white text-xs font-bold px-2.5 py-1.5 rounded-full shadow-lg">
              <FiZap className="text-xs" />
              DIGITAL
            </div>
          )}

          {/* Discount Badge */}
          {hasPromotion && (
            <div className={`absolute ${isDigital ? 'top-14' : 'top-4'} left-4 bg-pagali-primary text-white px-3 py-1.5 rounded-full text-sm font-bold shadow-lg`}>
              -{discountPercent}%
            </div>
          )}

          {/* Share Button */}
          <button
            onClick={handleShare}
            className="absolute top-4 right-4 w-10 h-10 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center text-pagali-gray-600 hover:bg-white shadow-md transition-colors"
          >
            <FiShare2 />
          </button>
        </div>

        {/* Details */}
        <div className="space-y-5">
          {/* Category */}
          {product.CATEGORIA && (
            <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-pagali-purple/10 text-pagali-purple text-sm rounded-full">
              <FiTag className="text-xs" />
              {product.CATEGORIA}
            </span>
          )}

          {/* Title */}
          <div className="flex items-start gap-3">
            <h1 className="text-2xl md:text-3xl font-bold text-pagali-gray-900">
              {product.NOME}
            </h1>
            {isDigital && (
              <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-blue-100 text-blue-700 text-xs font-semibold rounded-full whitespace-nowrap mt-1">
                <FiDownload className="text-xs" />
                Produto Digital
              </span>
            )}
          </div>

          {/* Price */}
          <div className="flex items-baseline gap-3">
            <span className={`text-3xl font-bold ${hasPromotion ? 'text-pagali-primary' : 'text-pagali-gray-900'}`}>
              {formatCurrency(currentPrice)}
            </span>
            {hasPromotion && (
              <span className="text-lg text-pagali-gray-400 line-through">
                {formatCurrency(price)}
              </span>
            )}
          </div>

          {/* Stock */}
          <div className="flex items-center gap-2">
            {isDigital ? (
              <span className="flex items-center gap-1.5 text-emerald-600 text-sm font-semibold">
                <FiZap />
                Entrega imediata
              </span>
            ) : isOutOfStock ? (
              <span className="flex items-center gap-1.5 text-pagali-error text-sm">
                <FiXCircle />
                Esgotado
              </span>
            ) : stock <= 5 ? (
              <span className="flex items-center gap-1.5 text-amber-600 text-sm">
                <FiAlertTriangle />
                Apenas {stock} em stock
              </span>
            ) : (
              <span className="flex items-center gap-1.5 text-green-600 text-sm">
                <FiCheck />
                Em stock
              </span>
            )}
          </div>

          {/* Description */}
          {product.DESCRICAO && (
            <div className="pt-2 border-t border-pagali-gray-100">
              <h3 className="text-sm font-semibold text-pagali-gray-700 mb-2">Descricao</h3>
              <div
                className="text-pagali-gray-600 text-sm leading-relaxed prose prose-sm max-w-none"
                dangerouslySetInnerHTML={{ __html: product.DESCRICAO }}
              />
            </div>
          )}

          {/* Product Info */}
          {(product.CODIGO || product.UNIDADE) && (
            <div className="pt-2 border-t border-pagali-gray-100">
              <div className="grid grid-cols-2 gap-3 text-sm">
                {product.CODIGO && (
                  <div>
                    <span className="text-pagali-gray-500">Codigo:</span>{' '}
                    <span className="font-medium text-pagali-gray-800">{product.CODIGO}</span>
                  </div>
                )}
                {product.UNIDADE && (
                  <div>
                    <span className="text-pagali-gray-500">Unidade:</span>{' '}
                    <span className="font-medium text-pagali-gray-800">{product.UNIDADE}</span>
                  </div>
                )}
                {product.IVA !== undefined && product.IVA !== null && (
                  <div>
                    <span className="text-pagali-gray-500">IVA:</span>{' '}
                    <span className="font-medium text-pagali-gray-800">{product.IVA}%</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Add to Cart */}
          {!isOutOfStock && (
            <div className="pt-4 border-t border-pagali-gray-100 space-y-3">
              {/* Digital delivery info */}
              {isDigital && (
                <div className="flex items-center gap-2 px-3 py-2.5 bg-emerald-50 border border-emerald-200 rounded-xl text-sm text-emerald-700">
                  <FiZap className="flex-shrink-0" />
                  <span>Produto digital — recebes o link de acesso por email após pagamento</span>
                </div>
              )}
              {/* Quantity — hidden for digital products */}
              {!isDigital && (
                <div className="flex items-center gap-4">
                  <span className="text-sm font-medium text-pagali-gray-700">Quantidade:</span>
                  <div className="flex items-center gap-1 bg-pagali-gray-100 rounded-xl p-1">
                    <button
                      onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                      className="w-9 h-9 rounded-lg bg-white flex items-center justify-center text-pagali-gray-600 hover:text-pagali-primary shadow-sm transition-colors"
                    >
                      <FiMinus className="text-sm" />
                    </button>
                    <span className="w-12 text-center font-semibold text-pagali-gray-800">
                      {quantity}
                    </span>
                    <button
                      onClick={() => setQuantity((q) => (stock ? Math.min(stock, q + 1) : q + 1))}
                      className="w-9 h-9 rounded-lg bg-white flex items-center justify-center text-pagali-gray-600 hover:text-pagali-primary shadow-sm transition-colors"
                    >
                      <FiPlus className="text-sm" />
                    </button>
                  </div>
                  <span className="text-sm text-pagali-gray-500 ml-auto">
                    Subtotal: <span className="font-semibold text-pagali-gray-800">{formatCurrency(currentPrice * quantity)}</span>
                  </span>
                </div>
              )}

              {/* Add Button */}
              <button
                onClick={handleAddToCart}
                disabled={addedToCart}
                className={`w-full py-3.5 rounded-2xl font-semibold text-white flex items-center justify-center gap-2 transition-all ${
                  addedToCart
                    ? 'bg-green-500'
                    : 'bg-pagali-purple hover:bg-pagali-purple-light active:scale-[0.98]'
                }`}
              >
                {addedToCart ? (
                  <>
                    <FiCheck className="text-lg" />
                    Adicionado ao carrinho!
                  </>
                ) : isDigital ? (
                  <>
                    <FiShoppingCart className="text-lg" />
                    Comprar Agora
                  </>
                ) : (
                  <>
                    <FiShoppingCart className="text-lg" />
                    Adicionar ao Carrinho
                  </>
                )}
              </button>

              {inCart && !addedToCart && (
                <p className="text-center text-sm text-pagali-gray-500">
                  Ja tens {inCart.QTD} no carrinho
                </p>
              )}
            </div>
          )}

          {/* Store Info */}
          {product.LOJA_NOME && (
            <div className="pt-4 border-t border-pagali-gray-100">
              <button
                onClick={() => navigate(`/ecommerce/store/${storeId}`)}
                className="flex items-center gap-3 p-3 bg-pagali-gray-50 rounded-xl hover:bg-pagali-gray-100 transition-colors w-full text-left"
              >
                <div className="w-10 h-10 rounded-lg bg-white overflow-hidden flex items-center justify-center shadow-sm">
                  {lojaLogoSrc ? (
                    <img src={lojaLogoSrc} alt={product.LOJA_NOME} className="w-full h-full object-cover" />
                  ) : (
                    <IoStorefrontOutline className="text-lg text-pagali-gray-400" />
                  )}
                </div>
                <div>
                  <p className="text-sm font-semibold text-pagali-gray-800">{product.LOJA_NOME}</p>
                  <p className="text-xs text-pagali-gray-500">Ver todos os produtos da loja</p>
                </div>
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Floating Cart Bar */}
      {itemCount > 0 && (
        <button
          onClick={() => navigate('/ecommerce/checkout')}
          className="fixed bottom-6 left-1/2 -translate-x-1/2 bg-pagali-purple text-white rounded-full px-6 py-3 shadow-modal flex items-center gap-3 z-40 hover:bg-pagali-purple-light transition-colors"
        >
          <FiShoppingCart className="text-lg" />
          <span className="font-medium">Ver Carrinho</span>
          <span className="bg-white/20 px-2 py-0.5 rounded-full text-sm">
            {itemCount} {itemCount === 1 ? 'item' : 'items'}
          </span>
          <span className="font-semibold">{cartTotal.toLocaleString('pt-CV')} ECV</span>
        </button>
      )}
    </div>
  );
};

export default ProductDetail;
